import React from "react";
import clone from "lodash/clone";
import cloneDeep from "lodash/cloneDeep";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmLeaveDialog } from "components/common";
import Routes from "routing/Routes";
import {
  createEventNotification,
  updateEventNotification,
  testEventNotification,
  EVENT_NOTIFICATIONS_QUERY_KEY
} from "components/event-notifications/hooks/useEventNotifications";
import withHistory from "routing/withHistory";
import CurrentUser from "logic/users/CurrentUser";
import EventNotificationForm from "./EventNotificationForm";
const initialValidation = {
  errors: {}
};
const initialTestResult = {
  isLoading: false
};
const _EventNotificationFormContainer = class _EventNotificationFormContainer extends React.Component {
  constructor(props) {
    super(props);
    this.handleChange = (key, value) => {
      const { notification } = this.state;
      const nextNotification = cloneDeep(notification);
      nextNotification[key] = value;
      this.setState({ notification: nextNotification, isDirty: true, testResult: initialTestResult });
    };
    this.handleCancel = () => {
      const { history } = this.props;
      history.push(Routes.ALERTS.NOTIFICATIONS.LIST);
    };
    this.handleSubmit = () => {
      const { action, embedded, onSubmit, history, queryClient } = this.props;
      const { notification } = this.state;
      this.setState({ isDirty: false });
      let promise;
      if (action === "create") {
        promise = createEventNotification(notification);
        promise.then((response) => {
          queryClient.invalidateQueries({ queryKey: EVENT_NOTIFICATIONS_QUERY_KEY });
          CurrentUser.reload();
          return response;
        }).then(
          () => {
            if (!embedded) {
              history.push(Routes.ALERTS.NOTIFICATIONS.LIST);
            }
          },
          (errorResponse) => {
            const { body } = errorResponse.additional;
            if (errorResponse.status === 400 && body && body.failed) {
              this.setState({ validation: body });
              _EventNotificationFormContainer.scrollToFirstError();
            }
          }
        );
      } else {
        promise = updateEventNotification(notification.id, notification);
        promise.then(
          () => {
            queryClient.invalidateQueries({ queryKey: EVENT_NOTIFICATIONS_QUERY_KEY });
            if (!embedded) {
              history.push(Routes.ALERTS.NOTIFICATIONS.LIST);
            }
          },
          (errorResponse) => {
            const { body } = errorResponse.additional;
            if (errorResponse.status === 400 && body && body.failed) {
              this.setState({ validation: body });
              _EventNotificationFormContainer.scrollToFirstError();
            }
          }
        );
      }
      onSubmit(promise);
    };
    this.handleTest = () => {
      const { notification } = this.state;
      this.setState({ testResult: { isLoading: true }, validation: initialValidation });
      const testResult = clone(initialTestResult);
      this.testPromise = testEventNotification(notification);
      this.testPromise.then(
        (response) => {
          testResult.error = false;
          testResult.message = "Notification was executed successfully.";
          return response;
        },
        (errorResponse) => {
          testResult.error = true;
          const { body } = errorResponse.additional;
          if (errorResponse.status === 400 && body && body.failed) {
            testResult.message = "Validation failed, please correct any errors in the form before continuing.";
            this.setState({ validation: body });
          } else {
            testResult.message = errorResponse.responseMessage || "Unknown error, please check your server logs.";
          }
        }
      ).finally(() => {
        this.setState({ testResult });
        this.testPromise = void 0;
      });
    };
    this.state = {
      notification: props.notification,
      validation: initialValidation,
      testResult: initialTestResult,
      isDirty: false
    };
  }
  static scrollToFirstError() {
    if (document.getElementsByClassName("has-error")[0] !== void 0) {
      document.getElementsByClassName("has-error")[0].scrollIntoView(true);
    }
  }
  componentWillUnmount() {
    if (this.testPromise) {
      this.testPromise.cancel();
    }
  }
  render() {
    const { action, embedded, formId } = this.props;
    const { notification, validation, testResult, isDirty } = this.state;
    return /* @__PURE__ */ React.createElement(React.Fragment, null, !embedded && isDirty && /* @__PURE__ */ React.createElement(ConfirmLeaveDialog, { question: "Do you really want to abandon this page and lose your changes? This action cannot be undone." }), /* @__PURE__ */ React.createElement(
      EventNotificationForm,
      {
        action,
        notification,
        validation,
        testResult,
        formId,
        embedded,
        onChange: this.handleChange,
        onCancel: this.handleCancel,
        onSubmit: this.handleSubmit,
        onTest: this.handleTest
      }
    ));
  }
};
_EventNotificationFormContainer.defaultProps = {
  action: "edit",
  notification: {
    title: "",
    description: "",
    config: {}
  },
  embedded: false,
  formId: void 0,
  onSubmit: () => {
  }
};
let EventNotificationFormContainer = _EventNotificationFormContainer;
const EventNotificationFormContainerWithHistory = withHistory(EventNotificationFormContainer);
const EventNotificationFormContainerWithQueryClient = (props) => {
  const queryClient = useQueryClient();
  return /* @__PURE__ */ React.createElement(EventNotificationFormContainerWithHistory, { ...props, queryClient });
};
export default EventNotificationFormContainerWithQueryClient;
